import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  api,
  STATUS_LABELS,
  STATUS_COLORS,
  MATERIAL_COLORS,
  OUTCOME_LABELS,
  OUTCOME_COLORS,
  formatApiError,
} from "@/lib/apiClient";
import {
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Pencil,
  Plus,
  ExternalLink,
  Trash2,
  Calendar,
  StickyNote,
  Save,
  Check,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SharePricingButtons from "@/components/SharePricingButtons";
import VoiceDictate from "@/components/VoiceDictate";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [error, setError] = useState("");

  // État inline pour l'édition des notes
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSavedOk, setNotesSavedOk] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([api.get(`/clients/${id}`), api.get(`/visits?client_id=${id}`)]);
      setClient(c.data);
      setVisits(v.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const fullAddress = useMemo(
    () => (client ? [client.address, client.postal_code, client.city].filter(Boolean).join(", ") : ""),
    [client],
  );

  const mapsHref = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;
  const wazeHref = fullAddress ? `https://www.waze.com/ul?q=${encodeURIComponent(fullAddress)}&navigate=yes` : null;

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-rose-600 font-bold mb-4">{error}</p>
        <Link to="/clients" className="text-emerald-600 font-bold">Retour à la liste</Link>
      </div>
    );
  }

  if (!client) return <div className="text-stone-400 text-center py-10">Chargement...</div>;

  const deleteVisit = async (vid) => {
    if (!window.confirm("Supprimer cette visite ?")) return;
    await api.delete(`/visits/${vid}`);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-3">
        <Link
          to={`/clients/${id}/edit`}
          data-testid="edit-client-btn"
          className="inline-flex items-center gap-2 bg-white border border-stone-200 hover:bg-stone-50 text-stone-800 font-bold px-4 py-2 rounded-full text-sm"
        >
          <Pencil className="w-4 h-4" strokeWidth={2.5} /> Modifier
        </Link>
      </div>

      {/* Header card */}
      <div className="bg-white rounded-3xl border border-stone-100 p-5 sm:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white grid place-items-center font-heading font-black text-xl shadow-lg shadow-emerald-500/30">
            {client.company_name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-black text-2xl tracking-tight" data-testid="client-company-name">
              {client.company_name}
            </h1>
            {client.contact_name && <p className="text-stone-600">{client.contact_name}</p>}
            <div className="mt-2">
              <span className={`px-2.5 py-0.5 rounded-full border text-xs font-bold ${STATUS_COLORS[client.status]}`}>
                {STATUS_LABELS[client.status]}
              </span>
            </div>
          </div>
        </div>

        {/* Contact rows */}
        <div className="mt-5 space-y-2">
          {client.phone && (
            <a
              href={`tel:${client.phone}`}
              data-testid="client-phone-link"
              className="flex items-center gap-3 p-3 rounded-2xl bg-stone-50 hover:bg-stone-100 transition-colors"
            >
              <Phone className="w-5 h-5 text-stone-500" strokeWidth={2.5} />
              <span className="font-bold">{client.phone}</span>
            </a>
          )}
          {client.email && (
            <a
              href={`mailto:${client.email}`}
              data-testid="client-email-link"
              className="flex items-center gap-3 p-3 rounded-2xl bg-stone-50 hover:bg-stone-100 transition-colors"
            >
              <Mail className="w-5 h-5 text-stone-500" strokeWidth={2.5} />
              <span className="font-bold break-all">{client.email}</span>
            </a>
          )}
          {fullAddress && (
            <div className="p-3 rounded-2xl bg-stone-50">
              <div className="flex items-start gap-3 mb-2">
                <MapPin className="w-5 h-5 text-stone-500 shrink-0 mt-0.5" strokeWidth={2.5} />
                <span className="font-bold leading-snug">{fullAddress}</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-google-maps"
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm px-4 py-2 rounded-full transition-all active:scale-95 shadow-lg shadow-emerald-500/30"
                >
                  <MapPin className="w-4 h-4" strokeWidth={2.5} /> Google Maps
                  <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
                </a>
                <a
                  href={wazeHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-waze"
                  className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm px-4 py-2 rounded-full transition-all active:scale-95 shadow-lg shadow-sky-500/30"
                >
                  Waze <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Bloc notes éditable */}
        <div className="mt-4 p-4 rounded-2xl bg-amber-50 border border-amber-100">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-amber-800 font-bold text-sm">
              <StickyNote className="w-4 h-4" strokeWidth={2.5} /> Notes
            </div>
            <div className="flex items-center gap-2">
              {notesSavedOk && !notesEditing && (
                <span className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} /> Enregistré
                </span>
              )}
              {notesEditing ? (
                <VoiceDictate
                  onTranscript={(text) =>
                    setNotesDraft((prev) => (prev ? prev + " " + text : text))
                  }
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setNotesDraft(client.notes || "");
                    setNotesEditing(true);
                    setNotesSavedOk(false);
                  }}
                  data-testid="notes-edit-btn"
                  className="inline-flex items-center gap-1.5 bg-amber-200 hover:bg-amber-300 text-amber-900 font-bold px-3 py-1.5 rounded-full text-xs transition-all active:scale-95"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={2.8} />
                  Modifier
                </button>
              )}
            </div>
          </div>

          {notesEditing ? (
            <div>
              <textarea
                data-testid="notes-textarea"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={5}
                autoFocus
                placeholder="Tape ou utilise le micro 🎤 pour dicter..."
                className="w-full bg-white border border-amber-200 focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none rounded-xl px-3 py-2 text-sm transition-all"
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setNotesEditing(false);
                    setNotesDraft("");
                  }}
                  className="inline-flex items-center gap-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold px-3 py-1.5 rounded-full text-xs"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  data-testid="notes-save-btn"
                  disabled={notesSaving}
                  onClick={async () => {
                    setNotesSaving(true);
                    try {
                      const { data } = await api.patch(`/clients/${id}`, { notes: notesDraft });
                      setClient(data);
                      setNotesEditing(false);
                      setNotesSavedOk(true);
                      setTimeout(() => setNotesSavedOk(false), 2500);
                    } finally {
                      setNotesSaving(false);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-bold px-3 py-1.5 rounded-full text-xs active:scale-95"
                >
                  <Save className="w-3.5 h-3.5" strokeWidth={2.8} />
                  {notesSaving ? "..." : "Enregistrer"}
                </button>
              </div>
            </div>
          ) : client.notes ? (
            <p
              data-testid="notes-display"
              onClick={() => {
                setNotesDraft(client.notes || "");
                setNotesEditing(true);
              }}
              className="text-stone-700 whitespace-pre-wrap text-sm cursor-text hover:bg-amber-100/50 rounded-lg p-1 -m-1 transition-colors"
            >
              {client.notes}
            </p>
          ) : (
            <button
              type="button"
              data-testid="notes-add-btn"
              onClick={() => {
                setNotesDraft("");
                setNotesEditing(true);
              }}
              className="w-full text-left text-sm text-amber-700 hover:text-amber-900 italic"
            >
              + Ajoute une note (texte ou voix...)
            </button>
          )}
        </div>
      </div>

      {/* Partage tarifs */}
      <div className="bg-white rounded-3xl border border-stone-100 p-5 sm:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
        <SharePricingButtons
          user={user}
          client={client}
          label={`Envoyer mes tarifs à ${client.company_name}`}
        />
      </div>

      {/* Visits */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-black text-lg">Visites ({visits.length})</h3>
          <Link
            to={`/visites/nouvelle?client_id=${id}`}
            data-testid="add-visit-for-client"
            className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white font-bold px-3 py-2 rounded-full text-sm active:scale-95"
          >
            <Plus className="w-4 h-4" strokeWidth={3} /> Visite
          </Link>
        </div>

        {visits.length === 0 ? (
          <div className="bg-white rounded-3xl border border-dashed border-stone-200 p-8 text-center text-stone-500">
            Aucune visite enregistrée.
          </div>
        ) : (
          <div className="space-y-3">
            {visits.map((v) => (
              <div
                key={v.id}
                data-testid={`visit-${v.id}`}
                className="bg-white rounded-3xl border border-stone-100 p-4 shadow-[0_4px_20px_rgb(0,0,0,0.04)]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 text-stone-600 text-sm font-bold">
                    <Calendar className="w-4 h-4" strokeWidth={2.5} />
                    {new Date(v.visit_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                  <button
                    onClick={() => deleteVisit(v.id)}
                    data-testid={`delete-visit-${v.id}`}
                    className="text-stone-400 hover:text-rose-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>

                <div className="mt-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${OUTCOME_COLORS[v.outcome]}`}>
                    {OUTCOME_LABELS[v.outcome]}
                  </span>
                </div>

                {v.materials?.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {v.materials.map((m, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${MATERIAL_COLORS[m.type]}`}>
                          {m.type}
                        </span>
                        <span className="text-stone-700 font-medium tabular-nums">
                          {m.tonnage} t × {m.price_per_ton} €
                        </span>
                        <span className="ml-auto font-bold text-emerald-700 tabular-nums">
                          {(m.tonnage * m.price_per_ton).toLocaleString("fr-FR")} €
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {v.total_value > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-sm">
                    <span className="text-stone-500 font-bold">Total estimé</span>
                    <span className="font-heading font-black text-lg text-emerald-600">
                      {v.total_value.toLocaleString("fr-FR")} €
                    </span>
                  </div>
                )}

                {v.notes && <p className="mt-3 text-sm text-stone-700 whitespace-pre-wrap">{v.notes}</p>}

                {v.photos?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {v.photos.map((p, i) => (
                      <a
                        key={i}
                        href={p}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`visit-photo-${i}`}
                        className="block w-20 h-20 rounded-2xl overflow-hidden bg-stone-100 border border-stone-200 hover:ring-2 hover:ring-emerald-400 transition-all"
                      >
                        <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}

                {v.next_visit_date && (
                  <div className="mt-3 text-xs text-stone-500 inline-flex items-center gap-1 font-bold">
                    <Calendar className="w-3.5 h-3.5" strokeWidth={2.5} /> Prochaine visite :{" "}
                    {new Date(v.next_visit_date).toLocaleDateString("fr-FR")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
